import {
  Button,
  Drawer,
  Form,
  getValueFromEvent,
  Grid,
  Input,
  InputNumber,
  notification,
  Select,
  Space,
  Upload,
} from '@pankod/refine-antd';
import { file2Base64 } from '@pankod/refine-core';
import { EPaymentType } from 'enums';
import { useEffect, useState } from 'react';
import { getAllCompanyConfig } from 'services/config';
import { getPaymentMethods } from 'services/payment';
import {
  beforeReceiptUploadValidation,
  isLessThanSize,
  isPurchaseReceiptFile,
  MAX_UPLOAD_FILE_SIZE_MB,
} from 'utilities/file';
import styles from './style.module.css';

interface IPaymentMethodOption {
  id: string;
  name: string;
}

export interface IFormValues {
  attachments: any[];
  paymentType: EPaymentType;
  paymentMethod: string;
  companyBank: string;
  paymentDetails: string;
  currentPaidAmount: number;
  phoneNumber?: string | undefined;
  transactionId?: string | undefined;
  cardNumber?: string | undefined;
  approvalCode?: string | undefined;
}

type MakePaymentProps = {
  paymentBy: 'client' | 'supplier';
  paymentById: string;
  totalPayableAmount: number;
  visible: boolean;
  loading: boolean;
  defaultAmount?: number;
  closeDrawer: () => void;
  onPaymentSubmit: (data: IFormValues) => Promise<void>;
};

const { Option } = Select;

export const MakePayment: React.FC<MakePaymentProps> = (props) => {
  const [isPaymentMethodDisable, setIsPaymentMethodDisable] = useState<boolean>(false);
  const [paymentMethods, setPaymentMethods] = useState<IPaymentMethodOption[]>([]);
  const [companyPaymentMethods, setCompanyPaymentMethods] = useState<IPaymentMethodOption[]>([]);

  const [form] = Form.useForm();
  const breakpoint = Grid.useBreakpoint();
  const salesPaymentTypes = [
    EPaymentType.Cash,
    EPaymentType.Cheque,
    EPaymentType.Card,
    EPaymentType.Bkash,
    EPaymentType.Nagad,
  ];
  const purchasePaymentTypes = [EPaymentType.Cash, EPaymentType.Cheque];
  const paymentTypes = props.paymentBy === 'client' ? salesPaymentTypes : purchasePaymentTypes;
  const paymentType = Form.useWatch('paymentType', form);

  useEffect(() => {
    if (!isPaymentMethodDisable) return;

    (async () => {
      try {
        const data = await getAllCompanyConfig();
        const companyId = data[0].id;
        const [paymentMethodsResponse, companyPaymentMethodsResponse] = await Promise.all([
          getPaymentMethods(props.paymentBy, props.paymentById),
          getPaymentMethods('company', companyId),
        ]);

        const extendedPaymentMethods = paymentMethodsResponse.map((paymentMethod) => ({
          id: paymentMethod.id,
          name: paymentMethod.bankName,
        }));

        const extendedCompanyPaymentMethods = companyPaymentMethodsResponse.map(
          (paymentMethod) => ({
            id: paymentMethod.id,
            name: paymentMethod.bankName,
          })
        );

        setPaymentMethods(extendedPaymentMethods);
        setCompanyPaymentMethods(extendedCompanyPaymentMethods);
      } catch (err) {
        if (err instanceof Error) {
          notification.error({
            message: 'Failed',
            description: err.message,
          });
        }
        setPaymentMethods([]);
        setCompanyPaymentMethods([]);
      }
    })();
  }, [isPaymentMethodDisable, props.paymentBy, props.paymentById]);

  const onCloseButtonClick = () => {
    form.resetFields();
    props.closeDrawer();
  };

  const getOptions = (items: IPaymentMethodOption[]) => {
    return items.map((item) => (
      <Option key={item.id} value={item.id}>
        {item.name}
      </Option>
    ));
  };

  const getOptionsCompany = (items: IPaymentMethodOption[]) => {
    return items.map((item) => (
      <Option key={item.id} value={item.id}>
        {item.name}
      </Option>
    ));
  };

  const onPaymentTypeSelect = (value: EPaymentType) => {
    form.resetFields(['paymentMethod', 'companyBank']);
    if (value === EPaymentType.Cheque) {
      setIsPaymentMethodDisable(false);
    } else {
      setIsPaymentMethodDisable(true);
    }
  };

  const onFinish = async (values: IFormValues) => {
    const { currentPaidAmount, attachments } = values;
    const base64Files = [];

    if (currentPaidAmount === 0) {
      form.setFields([
        {
          name: 'currentPaidAmount',
          errors: ['Payment needs to be greater than 0'],
        },
      ]);
      return;
    }

    if (currentPaidAmount > Number.parseFloat(props.totalPayableAmount.toFixed(2))) {
      form.setFields([
        {
          name: 'currentPaidAmount',
          errors: [`Payment exceed total pending amount (${props.totalPayableAmount})`],
        },
      ]);
      return;
    }

    if (attachments && attachments.length) {
      for (const file of attachments) {
        const format = file.type || '';
        const size = file.size || 0;
        if (isPurchaseReceiptFile(format) && isLessThanSize(size, MAX_UPLOAD_FILE_SIZE_MB)) {
          if (file.originFileObj) {
            const base64String = await file2Base64(file);
            const { originFileObj, ...rest } = file;
            base64Files.push({ ...rest, base64String });
          } else {
            base64Files.push(file);
          }
        }
      }
    }

    await props.onPaymentSubmit({ ...values, attachments: base64Files });
    onCloseButtonClick();
  };

  const clientOrSupplierPaymentMethodTitle =
    props.paymentBy.toLowerCase() === 'client' ? 'Paying Bank' : 'Receiving Bank';
  const companyPaymentMethodTitle =
    props.paymentBy.toLowerCase() === 'client' ? 'Receiving Bank' : 'Paying Bank';

  return (
    <Drawer
      title="Add a new payment"
      placement="right"
      width={breakpoint.sm ? '450px' : '100%'}
      visible={props.visible}
      onClose={onCloseButtonClick}
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={onFinish}
        initialValues={{ currentPaidAmount: props.defaultAmount }}
      >
        <Space direction="vertical" className={styles['file-upload']}>
          <Form.Item
            name="attachments"
            valuePropName="fileList"
            getValueFromEvent={getValueFromEvent}
            rules={[
              {
                required: false,
                message: 'Attachment is required',
              },
            ]}
          >
            <Upload.Dragger
              listType="picture"
              accept="image/jpeg, image/png, image/heic, image/heif, .heic, .heif, application/pdf"
              multiple
              maxCount={2}
              beforeUpload={beforeReceiptUploadValidation}
            >
              <p className="ant-upload-text">Drag & Drop</p>
              <p className="ant-upload-text">Image or PDF files in this area</p>
              <p className="ant-upload-hint">
                {`Files must be less than ${MAX_UPLOAD_FILE_SIZE_MB}MB and maximum file count is 2`}
              </p>
            </Upload.Dragger>
          </Form.Item>
        </Space>

        <Form.Item
          label="Payment Type"
          name="paymentType"
          rules={[{ required: true, message: 'Please select a type' }]}
        >
          <Select
            showSearch
            placeholder="Select a type"
            onSelect={onPaymentTypeSelect}
            filterOption={(input, option: any) =>
              option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {paymentTypes.map((item) => (
              <Option key={item} value={item}>
                {item}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Conditional fields for Bkash and Nagad */}
        {(paymentType === EPaymentType.Bkash || paymentType === EPaymentType.Nagad) && (
          <>
            <Form.Item
              label="Phone Number"
              name="phoneNumber"
              rules={[{ required: true, message: 'Please enter phone number' }]}
            >
              <Input prefix="XXX XXXX " maxLength={4} />
            </Form.Item>
            <Form.Item
              label="Transaction ID"
              name="transactionId"
              rules={[{ required: true, message: 'Please enter transaction ID' }]}
            >
              <Input placeholder="e.g. 456" />
            </Form.Item>
          </>
        )}

        {/* Conditional fields for Card */}
        {paymentType === EPaymentType.Card && (
          <>
            <Form.Item
              label="Card Number (4 digit)"
              name="cardNumber"
              rules={[{ required: true, message: 'Please enter card number' }]}
            >
              <Input prefix="XXXX XXXX " maxLength={4} />
            </Form.Item>
            <Form.Item
              label="Approval Code"
              name="approvalCode"
              rules={[{ required: true, message: 'Please enter approval code' }]}
            >
              <Input placeholder="e.g. 4567" />
            </Form.Item>
          </>
        )}

        {/* Conditional fields for Cheque */}
        {paymentType === EPaymentType.Cheque && (
          <>
            <Form.Item
              label={clientOrSupplierPaymentMethodTitle}
              name="paymentMethod"
              rules={[{ required: false, message: 'Please select a method' }]}
            >
              <Select
                showSearch
                placeholder="Select a method"
                filterOption={(input, option: any) =>
                  option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                disabled={isPaymentMethodDisable}
              >
                {getOptions(paymentMethods)}
              </Select>
            </Form.Item>
            <Form.Item
              label={companyPaymentMethodTitle}
              name="companyPaymentMethod"
              rules={[
                {
                  required: true && !isPaymentMethodDisable,
                  message: 'Please select a method',
                },
              ]}
            >
              <Select
                showSearch
                placeholder="Select a method"
                filterOption={(input, option: any) =>
                  option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                disabled={isPaymentMethodDisable}
              >
                {getOptionsCompany(companyPaymentMethods)}
              </Select>
            </Form.Item>
          </>
        )}

        <Form.Item label="Payment Details" name="paymentDetails">
          <Input.TextArea rows={2} />
        </Form.Item>

        <Form.Item
          label="Payment Amount"
          name="currentPaidAmount"
          rules={[{ required: true, type: 'number' }]}
        >
          <InputNumber style={{ width: '100%' }} precision={2} />
        </Form.Item>

        <Form.Item wrapperCol={{ span: 4, offset: 20 }}>
          <Button type="primary" htmlType="submit" loading={props.loading}>
            Save
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
};
